import { ComponentRef, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ModalResult } from '../interfaces/modal-result';
import { ConfirmationDialogComponent } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { ResultState } from '../enums/result-state.enum';
import { DialogService } from '@progress/kendo-angular-dialog';

@Injectable({ providedIn: 'root' })
export class ModalDialogService implements OnDestroy {
  _translateService = inject(TranslateService);
  dialogService = inject(DialogService);

  private readonly refreshSubject = new BehaviorSubject<boolean>(true);
  refresh$ = this.refreshSubject.asObservable();

  private _dialogSubject: Subject<ModalResult>;
  private readonly _unsubscribe: Subject<void> = new Subject();
  public customDialogRef: ComponentRef<any>;

  dialogParameters = signal(null);

  public showConfirmationBox(
    message: string,
    title: string,
    emphasizedText?: string
  ): Observable<ModalResult> {
    return this._translateService.get(['SHARED.YES', 'SHARED.NO']).pipe(
      mergeMap(localizedValues => {
        this.dialogParameters.set({
          showCancellationButton: true,
          customConfirmationLabel: localizedValues['SHARED.YES'],
          customCancellationLabel: localizedValues['SHARED.NO'],
          emphasizedText,
          message,
          title,
        });

        return this.showMessage();
      })
    );
  }

  private resetDialogInstance() {
    if (this._dialogSubject) {
      this.closeInternal(ResultState.Closed);
    }

    if (this.customDialogRef) {
      this.customDialogRef.destroy();
    }
  }

  public closeInternal(state: ResultState): void {
    const result: ModalResult = { data: null, state };
    this.close(result);
  }

  public showMessage(): Observable<ModalResult> {
    this.resetDialogInstance();

    const dialogRef = this.dialogService.open({
      content: ConfirmationDialogComponent,
      width: 300,
      height: 250,
    });

    dialogRef.result.pipe(takeUntil(this._unsubscribe)).subscribe(result => {
      this.close(result as ModalResult);
    });

    return this.open();
  }

  public close(result: ModalResult): void {
    if (this.customDialogRef) {
      this.customDialogRef.destroy();
      this.customDialogRef = null;
    }

    this.notifyDialogClosure(result);
  }

  public showCustomDialog(content: any, dialogParameters: any, width: number = 950) {
    this.dialogParameters.set(dialogParameters);

    this.resetDialogInstance();

    const dialogRef = this.dialogService.open({
      content,
      width,
    });

    dialogRef.result.pipe(takeUntil(this._unsubscribe)).subscribe(result => {
      this.close(result as ModalResult);
    });

    return this.open();
  }

  triggerRefresh() {
    this.refreshSubject.next(true);
  }

  private open(): Observable<ModalResult> {
    if (this._dialogSubject) {
      this.notifyDialogClosure({ data: null, state: ResultState.Cancelled });
    }

    this._dialogSubject = new Subject<ModalResult>();

    return this._dialogSubject.asObservable();
  }

  private notifyDialogClosure(result: ModalResult) {
    if (this._dialogSubject) {
      this._dialogSubject.next(result);
      this._dialogSubject.complete();
    }

    this.dialogParameters.set(null);
    this._dialogSubject = null;
  }

  public ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
